"use client";

import { createContext, useContext } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ModalData,
  useModal,
} from "@/app/(workspace)/_hooks/useModal";
import {
  UploadFileModal,
  CreateProjectModal,
  ProgressModal,
  MessageModal,
  EditTitleModal,
  LodBackfillModal,
} from "@/app/(workspace)/_components/modal";

interface ProjectModalContextProps {
  open: (data: ModalData, replace?: boolean) => void;
  close: () => void;
}

const ModalContext = createContext<ProjectModalContextProps | undefined>(
  undefined
);

const MODAL_COMPONENTS = {
  upload: UploadFileModal,
  create: CreateProjectModal,
  progress: ProgressModal,
  message: MessageModal,
  editTitle: EditTitleModal,
  lodBackfill: LodBackfillModal,
} as const;

const MODAL_SIZE_CLASSES = {
  sm: "w-[calc(100vw-2rem)] sm:max-w-md",
  md: "w-[calc(100vw-2rem)] sm:max-w-xl",
  lg: "w-[calc(100vw-2rem)] sm:max-w-2xl",
  xl: "w-[calc(100vw-2rem)] sm:max-w-4xl",
} as const;

const DIALOG_CLASSES = {
  backdrop:
    "fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity select-none dark:bg-black/75",
  container: "fixed inset-0 z-10 w-screen overflow-y-auto",
  wrapper: "flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0",
  panel:
    "relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl ring-1 ring-zinc-900/5 transition-all sm:my-8 sm:w-full dark:bg-zinc-900 dark:ring-white/10",
  header:
    "flex items-center justify-between px-6 py-4 border-b border-zinc-200 select-none dark:border-zinc-800",
  title: "text-xl font-semibold text-zinc-900 dark:text-white",
  closeButton:
    "rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-white",
} as const;

export const useProjectModal = () => {
  const context = useContext(ModalContext);
  if (!context)
    throw new Error("useProjectModal must be used within ModalProvider");
  return context;
};

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const { isOpen, modalData, openModal, closeModal } = useModal();

  const CurrentModal =
    modalData && modalData.type in MODAL_COMPONENTS
      ? MODAL_COMPONENTS[modalData.type as keyof typeof MODAL_COMPONENTS]
      : null;

  const sizeClass = modalData?.size
    ? MODAL_SIZE_CLASSES[modalData.size as keyof typeof MODAL_SIZE_CLASSES] ||
    MODAL_SIZE_CLASSES.md
    : MODAL_SIZE_CLASSES.md;

  const showCloseButton = modalData?.type !== "progress";

  return (
    <ModalContext.Provider value={{ open: openModal, close: closeModal }}>
      {children}

      <Dialog open={isOpen} onClose={() => { }} className="relative z-50">
        <DialogBackdrop className={DIALOG_CLASSES.backdrop} />

        <div className={DIALOG_CLASSES.container}>
          <div className={DIALOG_CLASSES.wrapper}>
            <DialogPanel className={`${DIALOG_CLASSES.panel} ${sizeClass}`}>
              {isOpen && modalData && (
                <>
                  {modalData.title && (
                    <div className={DIALOG_CLASSES.header}>
                      <h2 className={DIALOG_CLASSES.title}>
                        {modalData.title}
                      </h2>
                      {showCloseButton && (
                        <button
                          onClick={closeModal}
                          className={DIALOG_CLASSES.closeButton}
                          aria-label="Close modal"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  )}

                  {CurrentModal && (
                    <CurrentModal data={modalData} onClose={closeModal} />
                  )}
                </>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </ModalContext.Provider>
  );
};
